/**
 * Тема Service Desk: dark (по умолчанию) ↔ light.
 *
 * Архитектура:
 *  - Палитра — CSS-переменные в index.css (`:root` / `:root[data-theme="light"]`).
 *  - Tailwind extend.colors маппит семантические имена (bg, surface, fg,
 *    accent…) на эти переменные через tailwind.config.ts.
 *  - Этот провайдер только ставит `data-theme` на <html> и хранит выбор
 *    пользователя в localStorage.
 *  - Авто-детект `prefers-color-scheme` НЕ применяется по требованию
 *    (только ручной выбор).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'sd-theme';

interface ThemeCtx {
  theme: Theme;
  toggle: () => void;
  set: (t: Theme) => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

function readInitial(): Theme {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* localStorage может быть заблокирован — игнорируем */ }
  return 'dark';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readInitial);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  const toggle = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));
  return <Ctx.Provider value={{ theme, toggle, set: setTheme }}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme: ThemeProvider не подключён');
  return v;
}
